import {css} from 'aphrodite';
import React, {ReactElement} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import navigateToURL from 'lib/navigateToURL';
import * as sequtil from 'molbio/SeqUtil';
import {
  RelevantItemsFilteredTableState,
  RelevantItemsFilterOption,
} from 'relevant-items/PresentationalRelevantItemsSection';
import {HEADER_COLUMNS, HeaderInfo, NOT_SORTABLE} from 'relevant-items/RelevantItemsHeaderColumns';
import {
  RelevantRowData,
  matchName,
  matchModifiedAt,
  matchProjectFolder,
  matchReference,
  getSortedRelevantItemRowData,
  plainReferenceText,
} from 'relevant-items/shared/RelevantItemsTableUtils';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {Cell, CellProps, Column, simpleTableStyles, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';
import filterItemsToSearchQuery from 'util/filterItemsToSearchQuery';
import * as WorkflowActions from 'workflows/WorkflowActions';

import {
  RelevantItemsGraphQL,
  RelevantItemsGraphQL_RelevantItemsNode_tagged as TaggedItem,
  RelevantItemsGraphQL_RelevantItemsNode_filesMentioning as FilesMentioningItem,
  RelevantItemsGraphQL_RelevantItemsNode_sequencesUsingAsPrimer_SequenceUsingAsPrimerNode as SequenceUsingAsPrimer,
  RelevantItemsGraphQL_RelevantItemsNode_workflows_WorkflowRelevantItemNode as RelevantWorkflowGQL,
} from './__generated/RelevantItemsGraphQL';
import getRelevantWorkflowInfo from './getRelevantWorkflowInfo';
import {RelevantProcess} from './RelevantItemsConstants';

function getLocation(item: {
  id: string;
  folder?: {source?: {name: string}} | null;
  parent?: {folder?: {source?: {name: string}} | null; __typename?: string} | null;
}): string | null {
  return item.folder?.source?.name ?? item.parent?.folder?.source?.name ?? null;
}

function getBaseRelevantItemRowData(
  itemsToShow: RelevantItemsFilterOption,
  areConvertedRelevantItems: boolean,
  relevantItemsGraphQL: RelevantItemsGraphQL | null
): Array<RelevantRowData> {
  const {
    bulkAssembliesCreatingAsAmplifiedFragment,
    bulkAssembliesCreatingAsConstruct,
    bulkAssembliesUsingAsFragment,
    filesMentioning,
    sequencesUsingAsPrimer,
    sourceRuns,
    tagged,
    workflows,
  } =
    relevantItemsGraphQL != null
      ? relevantItemsGraphQL
      : {
          bulkAssembliesCreatingAsAmplifiedFragment: [],
          bulkAssembliesCreatingAsConstruct: [],
          bulkAssembliesUsingAsFragment: [],
          filesMentioning: [] as Array<FilesMentioningItem>,
          sequencesUsingAsPrimer: [],
          sourceRuns: [],
          tagged: [] as Array<TaggedItem>,
          workflows: [],
        };

  function filterItems<T extends {id: string; __typename?: string; parentId?: string}>(
    items: Array<T>
  ): Array<T> {
    switch (itemsToShow) {
      case RelevantItemsFilterOption.ALL:
        return items;
      case RelevantItemsFilterOption.BULK_ASSEMBLIES:
        return items.filter((f) => f.id && APIUtil.isBulkAssembly(f.id));
      case RelevantItemsFilterOption.ENTRIES:
        return items.filter((f) => f.id && APIUtil.isEntry(f.id));
      case RelevantItemsFilterOption.ENTITY_LINKS:
        return items.filter((f) => f.id && APIUtil.isBiologicalEntity(f.id));
      case RelevantItemsFilterOption.WORKSHEET_STEPS:
        return items.filter(
          (f) => f.__typename === 'EntryLikeDocumentNode' && f.parentId && APIUtil.isWorksheet(f.parentId)
        );
      case RelevantItemsFilterOption.LEGACY_WORKFLOWS:
        return [];
      case RelevantItemsFilterOption.SEQUENCE_PRIMERS:
        return [];
      case RelevantItemsFilterOption.PROCESSES:
        return [];
      case RelevantItemsFilterOption.RUNS:
        return [];
      default:
        throw new Error('Invalid relevant items filter option');
    }
  }

  const filterSequences = (items: Array<SequenceUsingAsPrimer>): Array<SequenceUsingAsPrimer> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.SEQUENCE_PRIMERS].includes(itemsToShow)
      ? items
      : [];

  const filterWorkflows = (items: Array<RelevantWorkflowGQL>): Array<RelevantWorkflowGQL> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.LEGACY_WORKFLOWS].includes(itemsToShow)
      ? items
      : [];

  const filterTaskGroups = (items: Array<RelevantProcess>): Array<RelevantProcess> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.PROCESSES].includes(itemsToShow) ? items : [];

  const filterRuns = <T,>(items: Array<T>): Array<T> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.RUNS].includes(itemsToShow) ? items : [];

  const entityLabel = areConvertedRelevantItems ? 'Converted item' : 'Entity';

  const taggedRows = filterItems(tagged).map(
    (item): RelevantRowData => ({
      id: item.id,
      name: item.name,
      referencedInText: plainReferenceText(`${entityLabel} referenced in field`),
      modifiedAt: item.modifiedAt,
      location: getLocation(item),
      chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.id);
      },
    })
  );

  const sequencesUsingAsPrimerRows = filterSequences(sequencesUsingAsPrimer).map(
    (item): RelevantRowData => ({
      id: item.sequence.id,
      name: item.sequence.name,
      referencedInText: plainReferenceText(
        `${entityLabel} bound as primer at position: ${
          item.bindStrand === 1 ? `+` : `-`
        }/${sequtil.modelToDisplay(item.bindPosition, item.sequence.startIndex)}`
      ),
      modifiedAt: item.sequence.modifiedAt,
      location: getLocation(item.sequence),
      chip: <BenchChip id={item.sequence.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.sequence.id);
      },
    })
  );

  const bulkAssembliesUsingAsFragmentRows = filterItems(bulkAssembliesUsingAsFragment).map((item) => ({
    id: item.id,
    name: item.name,
    referencedInText: plainReferenceText(`${entityLabel} used as fragment in assembly`),
    modifiedAt: item.modifiedAt,
    location: getLocation(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
    onClick: () => {
      FileBrowserActions.openFile(item.id);
    },
  }));

  const bulkAssembliesCreatingAsConstructRows = filterItems(bulkAssembliesCreatingAsConstruct).map(
    (item) => ({
      id: item.id,
      name: item.name,
      referencedInText: plainReferenceText(`${entityLabel} created by assembly`),
      modifiedAt: item.modifiedAt,
      location: getLocation(item),
      chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.id);
      },
    })
  );

  const bulkAssembliesCreatingAsAmplifiedFragmentRows = filterItems(
    bulkAssembliesCreatingAsAmplifiedFragment
  ).map((item) => ({
    id: item.id,
    name: item.name,
    referencedInText: plainReferenceText(`${entityLabel} created by assembly`),
    modifiedAt: item.modifiedAt,
    location: getLocation(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
    onClick: () => {
      FileBrowserActions.openFile(item.id);
    },
  }));

  const filesMentioningRows = filterItems(filesMentioning).map((item) => {
    let referencedInText: JSX.Element;
    if (item.__typename === 'EntryLikeDocumentNode' && item.parentId && APIUtil.isWorksheet(item.parentId)) {
      referencedInText = plainReferenceText(`${entityLabel} referenced in step '${item.name}'`);
    } else if (APIUtil.isEntry(item.id)) {
      referencedInText = plainReferenceText(`${entityLabel} referenced in entry`);
    } else {
      referencedInText = plainReferenceText(`${entityLabel} referenced in description`);
    }

    return {
      id: item.id,
      name: item.name,
      referencedInText,
      modifiedAt: item.modifiedAt,
      location: getLocation(item),
      chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.id);
      },
    };
  });

  const workflowsRows = filterWorkflows(workflows).map((item): RelevantRowData => {
    const {workflow} = item;

    const {description, modifiedAt, name, projectName} = getRelevantWorkflowInfo(
      item,
      areConvertedRelevantItems
    );

    return {
      id: workflow.id,
      name,
      referencedInText: description,
      modifiedAt,
      location: projectName,
      chip: (
        <Chip
          size={ChipSize.SMALL}
          text={name}
          icon="workflow"
          className="chip-container"
          onClick={() => {
            WorkflowActions.routeToWorkflow(item.workflow.id);
          }}
        />
      ),
      onClick: () => {
        WorkflowActions.routeToWorkflow(item.workflow.id);
      },
    };
  });

  const taskGroupsRows = relevantItemsGraphQL
    ? filterTaskGroups([...relevantItemsGraphQL.processOutputs, ...relevantItemsGraphQL.processTasks]).map(
        (item: RelevantProcess): RelevantRowData => {
          const {name, process} = item;
          const {modifiedAt, createdAt, editUrl, folder} = process;
          return {
            name,
            id: name,
            referencedInText: plainReferenceText(
              `${areConvertedRelevantItems ? 'Converted item' : 'Entity'} referenced in task group`
            ),
            modifiedAt: modifiedAt ?? createdAt,
            location: folder?.name ?? null,
            chip: (
              <Chip
                text={name}
                icon="process"
                size={ChipSize.SMALL}
                className="chip-container"
                onClick={() => {
                  navigateToURL(editUrl ?? '');
                  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_RELEVANT_ITEM, {
                    process_client_origin: item.process.processTaskSchema.clientOrigin,
                  });
                }}
              />
            ),
            onClick: () => {
              navigateToURL(editUrl ?? '');
            },
          };
        }
      )
    : [];

  const sourceRunsRows = filterRuns(sourceRuns).map(
    (item): RelevantRowData => ({
      id: item.apiId,
      name: item.name,
      referencedInText: plainReferenceText('Analysis created from run'),
      modifiedAt: item.modifiedAt,
      location: item.schema?.name ?? null,
      chip: <BenchChip id={item.apiId} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.apiId);
      },
    })
  );

  const allRows = taggedRows.concat(
    sequencesUsingAsPrimerRows,
    bulkAssembliesUsingAsFragmentRows,
    bulkAssembliesCreatingAsConstructRows,
    bulkAssembliesCreatingAsAmplifiedFragmentRows,
    filesMentioningRows,
    workflowsRows,
    taskGroupsRows,
    sourceRunsRows
  );
  return allRows;
}

function getRelevantItemRowData(props: {
  relevantItemsGraphQL: RelevantItemsGraphQL;
  relevantConvertedFromItemsGraphQL: RelevantItemsGraphQL | null;
  filteredTableState: RelevantItemsFilteredTableState;
}): Array<RelevantRowData> {
  const itemsToShow = props.filteredTableState.filterOption;
  const baseRelevantItemRows = getBaseRelevantItemRowData(itemsToShow, false, props.relevantItemsGraphQL);
  const convertedFromRelevantItemRows = getBaseRelevantItemRowData(
    itemsToShow,
    true,
    props.relevantConvertedFromItemsGraphQL
  );

  const rows = [...baseRelevantItemRows, ...convertedFromRelevantItemRows];
  return rows;
}

export interface RelevantItemsSectionTableProps {
  relevantItemsGraphQL: RelevantItemsGraphQL;
  relevantConvertedFromItemsGraphQL: RelevantItemsGraphQL | null;
  filteredTableState: RelevantItemsFilteredTableState;
  setFilteredTableState: (state: RelevantItemsFilteredTableState) => void;
  itemId: string;
  /** @default false */
  isCompact?: boolean;
  /** @default 100 */
  paginationLimit?: number;
}

/**
 * Table part of the relevant items section.
 *
 * @package This is meant for internal use, and should not generally be directly rendered; rather, use either
 * PresentationalRelevantItemsSection or RelevantItemsSection.
 *
 */
export default function RelevantItemsSectionTable({
  relevantItemsGraphQL,
  relevantConvertedFromItemsGraphQL,
  filteredTableState,
  setFilteredTableState,
  itemId,
  isCompact = false,
  paginationLimit = 100,
}: RelevantItemsSectionTableProps): JSX.Element {
  const {pageOffset, searchQuery, filterOption} = filteredTableState;

  const allRowData = getRelevantItemRowData({
    relevantItemsGraphQL,
    relevantConvertedFromItemsGraphQL,
    filteredTableState,
  });
  const filteredRowData = filterItemsToSearchQuery(allRowData, searchQuery, [
    matchName,
    matchModifiedAt,
    matchProjectFolder,
    matchReference,
  ]);
  const {sortState, getHeaderCellProps} = useSimpleTableSortState(itemId);
  const rowData = getSortedRelevantItemRowData(
    filteredRowData,
    sortState ?? {
      columnKey: HEADER_COLUMNS.modifiedAt.sortIndex,
      sortDirection: 'arrow-up',
    }
  );

  function renderHeader(headerInfo: HeaderInfo): ReactElement<CellProps> {
    assert(headerInfo.sortIndex !== NOT_SORTABLE, 'Expected header to have sortable index');

    return (
      <SortableHeaderCell {...getHeaderCellProps(headerInfo.sortIndex)}>{headerInfo.name}</SortableHeaderCell>
    );
  }

  function setOffset(pageOffset: number): void {
    setFilteredTableState({
      filterOption,
      searchQuery,
      pageOffset,
    });
  }

  return (
    <Table
      emptyTableText="No relevant items were found."
      numRows={Math.min(rowData.length - pageOffset, paginationLimit)}
      pagination={{offset: pageOffset, limit: paginationLimit, total: rowData.length}}
      setOffset={setOffset}
      isCompact={isCompact}
    >
      <Column
        header={renderHeader(HEADER_COLUMNS.item)}
        renderCell={(rowIndex) => <Cell>{rowData[pageOffset + rowIndex].chip}</Cell>}
      />
      <Column
        header={
          <Cell className={css(simpleTableStyles.grayHoverHeaderCell)}>{HEADER_COLUMNS.reference.name}</Cell>
        }
        renderCell={(rowIndex) => <Cell>{rowData[pageOffset + rowIndex].referencedInText}</Cell>}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.modifiedAt)}
        renderCell={(rowIndex) => (
          <Cell>
            {rowData[pageOffset + rowIndex].modifiedAt &&
              DateUtil.readableTime(rowData[pageOffset + rowIndex].modifiedAt)}
          </Cell>
        )}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.projectLocation)}
        renderCell={(rowIndex) => <Cell>{rowData[pageOffset + rowIndex].location}</Cell>}
      />
    </Table>
  );
}
